import React, { createContext, useContext, useCallback, useState, useMemo } from "react";
import { GlobalContext } from "../../context/GlobalContext";
import {
  getStudioUserToken,
  setStudioUserToken,
  clearStudioUserToken,
} from "../../utils/studioAuth";

function readStoredStudioUser() {
  try {
    const raw = sessionStorage.getItem("ahead_ai_studio_user_meta");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStoredStudioUser(user) {
  try {
    if (user) {
      sessionStorage.setItem(
        "ahead_ai_studio_user_meta",
        JSON.stringify(user),
      );
    } else {
      sessionStorage.removeItem("ahead_ai_studio_user_meta");
    }
  } catch {
    /* ignore */
  }
}

const AdminStudioContext = createContext(null);

export function AdminStudioProvider({ children }) {
  const { hitAxios } = useContext(GlobalContext);
  const [studioToken, setStudioTokenState] = useState(getStudioUserToken);
  const [studioUser, setStudioUser] = useState(readStoredStudioUser);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [acting, setActing] = useState(false);

  const refreshUsers = useCallback(async () => {
    setLoadingUsers(true);
    const res = await hitAxios({
      path: "/api/admin/get_users",
      post: false,
      admin: true,
      showLoading: false,
      showSnackbar: false,
    });
    if (res?.data?.success && Array.isArray(res.data.data)) {
      setUsers(res.data.data);
    }
    setLoadingUsers(false);
  }, [hitAxios]);

  React.useEffect(() => {
    refreshUsers();
  }, [refreshUsers]);

  const applyStudioUser = useCallback(
    async (uid) => {
      if (!uid) return false;
      setActing(true);
      const res = await hitAxios({
        path: "/api/admin/auto_login",
        post: true,
        admin: true,
        obj: { uid },
        showSnackbar: true,
      });
      setActing(false);
      if (res?.data?.success && res.data.token) {
        setStudioUserToken(res.data.token);
        setStudioTokenState(res.data.token);
        const u = users.find((x) => x.uid === uid);
        setStudioUser(
          u || {
            uid,
            name: res.data.name || uid,
            email: res.data.email || "",
          },
        );
        return true;
      }
      return false;
    },
    [hitAxios, users],
  );

  const clearStudioSession = useCallback(() => {
    clearStudioUserToken();
    writeStoredStudioUser(null);
    setStudioTokenState("");
    setStudioUser(null);
  }, []);

  const value = useMemo(
    () => ({
      studioToken,
      studioUser,
      hasStudioSession: Boolean(studioToken),
      users,
      loadingUsers,
      acting,
      refreshUsers,
      applyStudioUser,
      clearStudioSession,
    }),
    [
      studioToken,
      studioUser,
      users,
      loadingUsers,
      acting,
      refreshUsers,
      applyStudioUser,
      clearStudioSession,
    ],
  );

  return (
    <AdminStudioContext.Provider value={value}>
      {children}
    </AdminStudioContext.Provider>
  );
}

export function useAdminStudio() {
  const ctx = useContext(AdminStudioContext);
  if (!ctx) {
    throw new Error("useAdminStudio must be used within AdminStudioProvider");
  }
  return ctx;
}

export default AdminStudioContext;
